import { CONSTANTS_TIME__client } from '@/constants/CONSTANTS_TIME__client';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_ref_with_fallback } from '@/hooks/_base/use_ref_with_fallback';
import { use_hints_shortcuts_data } from '@/hooks/hints_shortcut/use_hints_shortcuts_data';
import { HOOK__run_on_next_frame } from '@/hooks/HOOK__run_on_next_frame';
import { use_slice_ui, use_slice_user } from '@/hooks/store/use_slices';
import { set_timeout } from '@/utils/utils_client';
import { IHintShortcutData } from '@shared/dto/hint_shortcuts/i_hint_shortcut_data';
import { useRef, useState } from 'react';
import { use_mouse_position_tracker } from './_hooks/use_mouse_position_tracker';
import { EScreenZone } from './e_screen_zone';
import { ViewHintShortcutGroup } from './view_hint_shortcut_group';

export function ViewHintsShortcutContainer() {
  const { user_settings } = use_slice_user(['user_settings']);
  const { hints_shortcut_visible } = use_slice_ui(['hints_shortcut_visible']);
  const { is_authorized } = use_slice_user(['is_authorized']);

  const { hint_shortcuts_data } = use_hints_shortcuts_data(hints_shortcut_visible);

  const [rendering_hints_top, set_rendering_hints_top] = useState<IHintShortcutData[]>([]);
  const [rendering_hints_bottom, set_rendering_hints_bottom] = useState<IHintShortcutData[]>([]);

  const { mouse_position } = use_mouse_position_tracker();

  const ref_zone_previous = use_ref_with_fallback<EScreenZone>(EScreenZone.TOP);

  const [is_adding_hints, set_is_adding_hints] = useState(false);

  const run_on_next_frame = HOOK__run_on_next_frame();

  //key - hint_key, value - timeout_id
  const ref_timeouts = useRef<Map<string, number>>(new Map());
  const ref_previous_hint_shortcuts = useRef<IHintShortcutData[]>([]);

  /*
   * EFFECTS
   */
  use_effect(() => {
    return () => {
      ref_timeouts.current.forEach(timeout_id => clearTimeout(timeout_id));
      ref_timeouts.current.clear();
    };
  });

  //todo: baz review rendering_hints_top+rendering_hints_bottom is not in deps
  use_effect(() => {
    if (ref_zone_previous.current === mouse_position.zone) return;

    let rendering_hints = mouse_position.zone === EScreenZone.TOP ? rendering_hints_top : rendering_hints_bottom;
    let screen_zone_to_delete_hints = mouse_position.zone === EScreenZone.TOP ? EScreenZone.TOP : EScreenZone.BOTTOM;

    rendering_hints.forEach(hint => schedule_removal(hint.key, screen_zone_to_delete_hints));

    ref_zone_previous.current = mouse_position.zone;
  }, [mouse_position]);

  //todo: baz review rendering_hints_top is not in deps
  use_effect(() => {
    let keys_previous = new Set(ref_previous_hint_shortcuts.current.map(item => item.key));
    let keys_current = new Set(hint_shortcuts_data.map(item => item.key));

    let new_hints = hint_shortcuts_data.filter(item => !keys_previous.has(item.key));

    if (new_hints.length > 0) {
      let zone_target = mouse_position.zone === EScreenZone.TOP ? EScreenZone.BOTTOM : EScreenZone.TOP;

      if (zone_target === EScreenZone.BOTTOM) set_is_adding_hints(true);

      let setter_rendering_hints = create_setter_rendering_hints(zone_target, new_hints);

      zone_target === EScreenZone.TOP ? set_rendering_hints_top(setter_rendering_hints) : set_rendering_hints_bottom(setter_rendering_hints);

      if (zone_target === EScreenZone.BOTTOM) run_on_next_frame(() => set_is_adding_hints(false));
    }

    for (let previous_hint of ref_previous_hint_shortcuts.current) {
      if (keys_current.has(previous_hint.key)) continue;

      let is_in_top_position = rendering_hints_top.some(h => h.key === previous_hint.key);
      let zone = is_in_top_position ? EScreenZone.TOP : EScreenZone.BOTTOM;
      schedule_removal(previous_hint.key, zone);
    }

    for (let current_hint of hint_shortcuts_data) {
      if (ref_timeouts.current.has(current_hint.key)) clear_timeout_for_key(current_hint.key);
    }

    ref_previous_hint_shortcuts.current = hint_shortcuts_data;
  }, [hint_shortcuts_data, mouse_position.zone]);

  /*
   * METHODS
   */

  function clear_timeout_for_key(key: string) {
    const timeout_id = ref_timeouts.current.get_value_or_default(key, NaN);

    clearTimeout(timeout_id);
    ref_timeouts.current.delete(key);
  }

  function schedule_removal(hint_key: string, zone: EScreenZone) {
    clear_timeout_for_key(hint_key);

    const timeout_id = set_timeout(() => {
      if (zone === EScreenZone.TOP) {
        set_rendering_hints_top(prev => prev.filter(item => item.key !== hint_key));
      } else {
        set_rendering_hints_bottom(prev => prev.filter(item => item.key !== hint_key));
      }
      ref_timeouts.current.delete(hint_key);
    }, CONSTANTS_TIME__client.TIMEOUT_ANIMATION_HINT_SHORTCUT_HIDE);

    ref_timeouts.current.set(hint_key, timeout_id);
  }

  function create_setter_rendering_hints(zone_target: EScreenZone, new_hints: IHintShortcutData[]) {
    return (previous: IHintShortcutData[]) => {
      const keys_existing = new Set(previous.map(item => item.key));
      const new_to_render = new_hints.filter(item => !keys_existing.has(item.key));

      let value_new = zone_target === EScreenZone.TOP ? [...new_to_render, ...previous] : [...previous, ...new_to_render];

      return new_to_render.length > 0 ? value_new : previous;
    };
  }

  /*
   * RENDERS
   */

  if (!user_settings.ui_hints_shortcuts_enabled) return null;
  if (!is_authorized) return null;

  return (
    <>
      <ViewHintShortcutGroup rendering_hints={rendering_hints_top} zone={EScreenZone.TOP} />

      <ViewHintShortcutGroup {...{ is_adding_hints }} rendering_hints={rendering_hints_bottom} zone={EScreenZone.BOTTOM} />
    </>
  );
}
